import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Globe, Verified, Share2, ExternalLink, Flag, CheckCircle, Star, Facebook, Instagram, Twitter, Linkedin, TrendingUp, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Reviews from '@/components/Reviews';
import FavoriteButton from '@/components/FavoriteButton';
import BusinessHoursDisplay from '@/components/BusinessHoursDisplay';
import CommunityVoting, { BadgeDisplay } from '@/components/CommunityVoting';
import LocationSelector from '@/components/LocationSelector';
import BoostBusinessButton from '@/components/BoostBusinessButton';
import ClaimBusinessWizard from '@/components/ClaimBusinessWizard';
import BusinessProducts from '@/components/BusinessProducts';
import { useFeatureFlags, useBusinessFeatures } from '@/hooks/useFeatureFlags';
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const PLACEHOLDER_IMAGES = [
  "https://images.pexels.com/photos/10375895/pexels-photo-10375895.jpeg",
  "https://images.unsplash.com/photo-1569598525846-cc400d20fac4?w=800",
  "https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg",
  "https://images.unsplash.com/photo-1709044472172-9b5af8b4491e?w=800"
];

export default function BusinessDetailPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [linkedBusiness, setLinkedBusiness] = useState(null);
  const [linkedLocations, setLinkedLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimStatus, setClaimStatus] = useState(null);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeSection, setActiveSection] = useState('about');
  const [similarBusinesses, setSimilarBusinesses] = useState([]);
  
  // Feature flags
  const { marketplaceEnabled } = useFeatureFlags();
  const businessFeatures = useBusinessFeatures(businessId);

  // Fetch similar businesses
  useEffect(() => {
    const fetchSimilar = async () => {
      try {
        const res = await fetch(`${API}/businesses/${businessId}/similar?limit=4`);
        if (res.ok) {
          const data = await res.json();
          setSimilarBusinesses(data.businesses || []);
        }
      } catch (err) {
        // Silent fail
      }
    };
    if (businessId) {
      fetchSimilar();
    }
  }, [businessId]);

  // Track analytics event
  const trackEvent = async (eventType, metadata = {}) => {
    try {
      await fetch(`${API}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_id: businessId,
          event_type: eventType,
          metadata
        })
      });
    } catch (error) {
      // Silent fail for analytics
    }
  };

  // Fetch linked businesses
  const fetchLinkedBusinesses = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/linked`);
      if (response.ok) {
        const data = await response.json();
        if (data.linked_online) {
          setLinkedBusiness(data.linked_online);
        } else if (data.linked_local) {
          setLinkedBusiness(data.linked_local);
          setLinkedLocations(data.locations || []);
        }
      }
    } catch (error) {
      // Silent fail for linked businesses
    }
  };

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await fetch(`${API}/businesses/${businessId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Business not found');
          } else {
            setError('Failed to load business');
          }
          return;
        }

        const data = await response.json();
        setBusiness(data);
        
        // Fetch linked businesses
        fetchLinkedBusinesses();
        
        // Track page view
        trackEvent('page_view');
        
        // Track recently viewed (for recommendations)
        try {
          fetch(`${API}/recently-viewed/${businessId}`, {
            method: 'POST',
            credentials: 'include'
          });
        } catch (e) {
          // Silent fail
        }

        // Check claim status
        try {
          const claimRes = await fetch(`${API}/businesses/${businessId}/claim-status`, {
            credentials: 'include'
          });
          if (claimRes.ok) {
            const claimData = await claimRes.json();
            setClaimStatus(claimData);
          }
        } catch (err) {
          console.log('Not logged in or claim check failed');
        }
      } catch (err) {
        console.error('Error fetching business:', err);
        setError('Failed to load business');
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [businessId]);

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: business.name,
          text: `Check out ${business.name} on Blackified`,
          url: url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };

  const handleClaimSuccess = () => {
    setClaimStatus(prev => ({ ...prev, has_pending_claim: true, claim_status: 'pending' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video skeleton rounded-lg mb-8" />
            <div className="h-8 w-64 skeleton rounded mb-4" />
            <div className="h-4 w-full skeleton rounded mb-2" />
            <div className="h-4 w-3/4 skeleton rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6">
          <div className="max-w-4xl mx-auto text-center py-20">
            <h1 className="text-2xl font-bold text-white mb-4">{error}</h1>
            <p className="text-zinc-500 mb-8">The business you&apos;re looking for might have been removed or doesn&apos;t exist.</p>
            <Link to="/businesses" className="btn-primary inline-block" data-testid="back-to-listings">
              Browse Businesses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const randomImage = PLACEHOLDER_IMAGES[Math.floor(Math.random() * PLACEHOLDER_IMAGES.length)];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-mono text-sm">Back</span>
            </button>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative aspect-video rounded-lg overflow-hidden mb-8"
          >
            <OptimizedImage
              src={business.image_url}
              fallback={randomImage}
              alt={business.name}
              preset="hero"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Verified Badge */}
            <div className="absolute top-4 left-4">
              <span className="verified-badge animate-pulse-glow">
                <Verified className="w-3 h-3" />
                Verified Business
              </span>
            </div>

            {/* Share Button */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              <FavoriteButton businessId={businessId} size="md" />
              <button
                onClick={handleShare}
                className="w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Business Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {/* Category */}
            <div className="flex items-center gap-3 mb-3">
              <p className="category-tag">{business.category}</p>
              {business.badge_level && <BadgeDisplay level={business.badge_level} />}
            </div>

            {/* Name */}
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
              {business.name}
            </h1>

            {/* Rating Summary */}
            {business.review_count > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(business.average_rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-zinc-600'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-white font-medium">{business.average_rating?.toFixed(1) || '0.0'}</span>
                <span className="text-zinc-500">({business.review_count} reviews)</span>
              </div>
            )}

            {/* Melanin Score & Boost */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              {business.melanin_score > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                  <span className="text-amber-400 font-medium">
                    {business.melanin_score.toFixed(1)} Score
                  </span>
                  <TrendingUp className="w-3 h-3 text-amber-400/60" />
                </div>
              )}
              <BoostBusinessButton
                businessId={businessId}
                businessName={business.name}
                melaninScore={business.melanin_score || 0}
                onBoostComplete={(newScore) => {
                  setBusiness({ ...business, melanin_score: newScore });
                }}
              />
            </div>

            {/* Location */}
            <div className="flex items-center gap-2 text-zinc-400 mb-8">
              <MapPin className="w-4 h-4" />
              <span>
                {[business.address, business.city, business.state, business.zip_code]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>

            {/* Claim Business Banner */}
            {!business.is_claimed && claimStatus && !claimStatus.is_owner && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-xl p-6 mb-8 border border-yellow-500/20"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center shrink-0">
                      <Flag className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">
                        Is this your business?
                      </h3>
                      <p className="text-zinc-400 text-sm">
                        Claim this listing to manage your business information, respond to reviews, and more.
                      </p>
                    </div>
                  </div>

                  {claimStatus.has_pending_claim ? (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10 text-yellow-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Claim Pending
                    </div>
                  ) : claimStatus.claim_status === 'rejected' ? (
                    <button
                      onClick={() => setClaimDialogOpen(true)}
                      className="btn-secondary text-sm py-2 px-4"
                      data-testid="claim-business-button"
                    >
                      Submit New Claim
                    </button>
                  ) : (
                    <button
                      onClick={() => setClaimDialogOpen(true)}
                      className="btn-primary text-sm py-2 px-4"
                      data-testid="claim-business-button"
                    >
                      Claim This Business
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Claimed Badge */}
            {business.is_claimed && claimStatus?.is_owner && (
              <div className="glass-card rounded-xl p-4 mb-8 border border-green-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-green-400 font-medium">You own this business</span>
                </div>
              </div>
            )}

            {/* Section Tabs */}
            {marketplaceEnabled && businessFeatures.marketplaceEnabled && (
              <div className="flex gap-2 mb-8 border-b border-zinc-800 pb-4">
                <button
                  onClick={() => setActiveSection('about')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeSection === 'about'
                      ? 'bg-white text-black'
                      : 'bg-zinc-900 text-zinc-400 hover:text-white'
                  }`}
                  data-testid="section-about"
                >
                  About
                </button>
                <button
                  onClick={() => setActiveSection('products')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeSection === 'products'
                      ? 'bg-white text-black'
                      : 'bg-zinc-900 text-zinc-400 hover:text-white'
                  }`}
                  data-testid="section-products"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Products
                </button>
                <button
                  onClick={() => setActiveSection('reviews')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeSection === 'reviews'
                      ? 'bg-white text-black'
                      : 'bg-zinc-900 text-zinc-400 hover:text-white'
                  }`}
                  data-testid="section-reviews"
                >
                  <Star className="w-4 h-4" />
                  Reviews
                </button>
              </div>
            )}

            {/* About Section (always shown when no marketplace or activeSection === 'about') */}
            {(!marketplaceEnabled || !businessFeatures.marketplaceEnabled || activeSection === 'about') && (
              <>
                {/* Description */}
                <div className="glass-card rounded-xl p-6 mb-8">
                  <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                    About
                  </h2>
                  <p className="text-zinc-300 leading-relaxed">
                    {business.description}
                  </p>
                </div>

            {/* Contact Info */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Contact
              </h2>
              
              <div className="space-y-4">
                {business.phone && (
                  <a
                    href={`tel:${business.phone}`}
                    onClick={() => trackEvent('phone_click')}
                    className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
                    data-testid="phone-link"
                  >
                    <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-zinc-800 transition-colors">
                      <Phone className="w-4 h-4" />
                    </div>
                    <span>{business.phone}</span>
                  </a>
                )}

                {business.website && (
                  <a
                    href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('website_click')}
                    className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
                    data-testid="website-link"
                  >
                    <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-zinc-800 transition-colors">
                      <Globe className="w-4 h-4" />
                    </div>
                    <span className="flex items-center gap-2">
                      {business.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </span>
                  </a>
                )}

                {business.address && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(
                      [business.address, business.city, business.state, business.zip_code]
                        .filter(Boolean)
                        .join(', ')
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('directions_click')}
                    className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
                    data-testid="map-link"
                  >
                    <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-zinc-800 transition-colors">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <span className="flex items-center gap-2">
                      Get Directions
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </span>
                  </a>
                )}

                {/* Online Business - Service Area */}
                {business.is_online_only && business.service_area && (
                  <div className="flex items-center gap-4 text-zinc-300">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Globe className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-purple-400 font-medium">Online Business</p>
                      <p className="text-zinc-500 text-sm">Ships to: {business.service_area}</p>
                    </div>
                  </div>
                )}

                {/* Linked Business Banner */}
                {linkedBusiness && (
                  <Link
                    to={`/businesses/${linkedBusiness.business_id}`}
                    className="block p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-colors"
                    data-testid="linked-business-banner"
                  >
                    <div className="flex items-center gap-3">
                      {business.is_online_only ? (
                        <>
                          <MapPin className="w-5 h-5 text-blue-400" />
                          <div className="flex-1">
                            <p className="text-white font-medium">Visit Our Physical Location{linkedLocations.length > 1 ? 's' : ''}</p>
                            <p className="text-zinc-400 text-sm">
                              {linkedLocations.length > 0 
                                ? `${linkedLocations.length} location${linkedLocations.length > 1 ? 's' : ''} available`
                                : linkedBusiness.address || 'See locations'}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Globe className="w-5 h-5 text-purple-400" />
                          <div className="flex-1">
                            <p className="text-white font-medium">Also Available Online</p>
                            <p className="text-zinc-400 text-sm">
                              Ships to: {linkedBusiness.service_area || 'Nationwide'}
                            </p>
                          </div>
                        </>
                      )}
                      <ExternalLink className="w-4 h-4 text-zinc-500" />
                    </div>
                  </Link>
                )}

                {/* Social Links */}
                {business.social_links && Object.values(business.social_links).some(v => v) && (
                  <div className="pt-4 border-t border-zinc-800">
                    <p className="text-zinc-500 text-sm mb-3">Follow Us</p>
                    <div className="flex items-center gap-2">
                      {business.social_links.facebook && (
                        <a
                          href={business.social_links.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('social_click', { platform: 'facebook' })}
                          className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-blue-400 hover:bg-zinc-800 transition-colors"
                          data-testid="social-facebook"
                        >
                          <Facebook className="w-4 h-4" />
                        </a>
                      )}
                      {business.social_links.instagram && (
                        <a
                          href={business.social_links.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('social_click', { platform: 'instagram' })}
                          className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-pink-400 hover:bg-zinc-800 transition-colors"
                          data-testid="social-instagram"
                        >
                          <Instagram className="w-4 h-4" />
                        </a>
                      )}
                      {business.social_links.twitter && (
                        <a
                          href={business.social_links.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('social_click', { platform: 'twitter' })}
                          className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-sky-400 hover:bg-zinc-800 transition-colors"
                          data-testid="social-twitter"
                        >
                          <Twitter className="w-4 h-4" />
                        </a>
                      )}
                      {business.social_links.linkedin && (
                        <a
                          href={business.social_links.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('social_click', { platform: 'linkedin' })}
                          className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-blue-500 hover:bg-zinc-800 transition-colors"
                          data-testid="social-linkedin"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Multiple Locations Selector */}
            <div className="mt-8">
              <LocationSelector 
                businessId={businessId}
                onLocationChange={(location) => setSelectedLocation(location)}
              />
            </div>

            {/* Business Hours */}
            {business.business_hours && (
              <div className="glass-card rounded-xl p-6 mt-8">
                <BusinessHoursDisplay hours={business.business_hours} />
              </div>
            )}

            {/* Community Voting */}
            <div className="mt-8">
              <CommunityVoting businessId={businessId} />
            </div>

            {/* Reviews (when no marketplace or activeSection === 'about') */}
            {(!marketplaceEnabled || !businessFeatures.marketplaceEnabled || activeSection === 'about') && (
              <div className="glass-card rounded-xl p-6 mt-8">
                <Reviews businessId={businessId} businessName={business.name} />
              </div>
            )}
              </>
            )}

            {/* Products Section */}
            {marketplaceEnabled && businessFeatures.marketplaceEnabled && activeSection === 'products' && (
              <div className="glass-card rounded-xl p-6" data-testid="products-section">
                <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">
                  Products & Services
                </h2>
                <BusinessProducts 
                  businessId={businessId} 
                  isOwner={claimStatus?.is_owner || false} 
                />
              </div>
            )}

            {/* Reviews Section (when marketplace enabled and reviews tab selected) */}
            {marketplaceEnabled && businessFeatures.marketplaceEnabled && activeSection === 'reviews' && (
              <div className="glass-card rounded-xl p-6" data-testid="reviews-section">
                <Reviews businessId={businessId} businessName={business.name} />
              </div>
            )}

            {/* Similar Businesses Section */}
            {similarBusinesses.length > 0 && (
              <div className="mt-8" data-testid="similar-businesses">
                <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                  Similar Businesses
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {similarBusinesses.map((biz) => (
                    <Link
                      key={biz.business_id}
                      to={`/businesses/${biz.business_id}`}
                      className="glass-card rounded-xl overflow-hidden hover:border-zinc-700 transition-colors group"
                      data-testid={`similar-${biz.business_id}`}
                    >
                      <div className="aspect-[16/9] overflow-hidden bg-zinc-900">
                        <OptimizedImage
                          src={biz.image_url}
                          fallback={PLACEHOLDER_IMAGES[Math.floor(Math.random() * PLACEHOLDER_IMAGES.length)]}
                          alt={biz.name}
                          preset="card"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-zinc-500 mb-1">{biz.category}</p>
                        <h3 className="font-semibold text-white truncate group-hover:text-zinc-300 transition-colors">
                          {biz.name}
                        </h3>
                        {biz.average_rating > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm text-zinc-400">
                              {biz.average_rating?.toFixed(1)} ({biz.review_count || 0})
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Claim Business Wizard */}
      <ClaimBusinessWizard
        business={business}
        isOpen={claimDialogOpen}
        onClose={() => setClaimDialogOpen(false)}
        onSuccess={handleClaimSuccess}
      />
    </div>
  );
}
